import { api } from "@/lib/apiClient";

export type PersonnelCostValueKind = "monthly_eur" | "percent_gross" | "hourly_eur";
export type PersonnelCostScope = "global" | "employee" | "department";
export type PersonnelCostDepartment = "monteur" | "technisches_buero" | "buchhaltung_personal";

export const PERSONNEL_DEPARTMENT_OPTIONS: Array<{
  value: PersonnelCostDepartment;
  label: string;
}> = [
  { value: "monteur", label: "Monteur" },
  { value: "technisches_buero", label: "Technisches Büro" },
  { value: "buchhaltung_personal", label: "Buchhaltung / Personal" },
];

export function personnelDepartmentLabel(value: string | null | undefined): string {
  if (!value) return "Nicht zugeordnet";
  return PERSONNEL_DEPARTMENT_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

export interface PersonnelCostCategory {
  id: number;
  slug: string;
  label: string;
  value_kind: PersonnelCostValueKind;
  is_system: boolean;
  default_percent: number | null;
  sort_index: number;
  active: boolean;
}

export interface PersonnelCostEntry {
  id: number;
  category_id: number;
  scope: PersonnelCostScope;
  user_login: string | null;
  departments: PersonnelCostDepartment[] | null;
  amount_monthly: number;
  percent_value: number | null;
  hourly_amount: number | null;
  note: string | null;
  updated_at: string | null;
  updated_by_login: string | null;
}

export interface PersonnelCostEmployeeSettings {
  user_login: string;
  gross_monthly: number;
  weekly_hours: number;
  vacation_days: number;
  bridge_holidays_days: number;
  target_hourly_rate: number | null;
  department: PersonnelCostDepartment | null;
  updated_at: string | null;
  updated_by_login: string | null;
}

export interface PersonnelCostCategoryCreate {
  slug?: string;
  label: string;
  value_kind?: PersonnelCostValueKind;
  default_percent?: number | null;
}

export interface PersonnelCostCategoryUpdate {
  label?: string;
  default_percent?: number | null;
  sort_index?: number;
  active?: boolean;
}

export interface PersonnelCostEntryUpsert {
  category_id: number;
  scope: PersonnelCostScope;
  user_login?: string | null;
  departments?: PersonnelCostDepartment[] | null;
  amount_monthly?: number;
  percent_value?: number | null;
  hourly_amount?: number | null;
  note?: string | null;
}

export interface PersonnelCostEmployeeSettingsUpsert {
  gross_monthly: number;
  weekly_hours: number;
  vacation_days: number;
  bridge_holidays_days: number;
  target_hourly_rate?: number | null;
  department?: PersonnelCostDepartment | null;
}

export const DEFAULT_EMPLOYEE_SETTINGS: Omit<PersonnelCostEmployeeSettingsUpsert, "target_hourly_rate"> = {
  gross_monthly: 0,
  weekly_hours: 40,
  vacation_days: 30,
  bridge_holidays_days: 10,
};

function buildQuery(params: Record<string, string | number | boolean | undefined | null>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }
  return parts.length ? `?${parts.join("&")}` : "";
}

export async function listPersonnelCostCategories(): Promise<PersonnelCostCategory[]> {
  return api.get<PersonnelCostCategory[]>("/personnel-costs/categories");
}

export async function createPersonnelCostCategory(
  payload: PersonnelCostCategoryCreate,
): Promise<PersonnelCostCategory> {
  return api.post<PersonnelCostCategory>("/personnel-costs/categories", payload);
}

export async function updatePersonnelCostCategory(
  id: number,
  payload: PersonnelCostCategoryUpdate,
): Promise<PersonnelCostCategory> {
  return api.put<PersonnelCostCategory>(`/personnel-costs/categories/${id}`, payload);
}

export async function deletePersonnelCostCategory(id: number): Promise<void> {
  await api.del(`/personnel-costs/categories/${id}`);
}

export async function listPersonnelCostEntries(userLogin?: string): Promise<PersonnelCostEntry[]> {
  return api.get<PersonnelCostEntry[]>(`/personnel-costs/entries${buildQuery({ user_login: userLogin })}`);
}

export async function upsertPersonnelCostEntries(
  entries: PersonnelCostEntryUpsert[],
): Promise<PersonnelCostEntry[]> {
  const res = await api.put<{ entries?: PersonnelCostEntry[] } | PersonnelCostEntry[]>(
    "/personnel-costs/entries",
    { entries },
  );
  return Array.isArray(res) ? res : (res.entries ?? []);
}

export async function listPersonnelCostEmployeeSettings(): Promise<PersonnelCostEmployeeSettings[]> {
  return api.get<PersonnelCostEmployeeSettings[]>("/personnel-costs/employee-settings");
}

export async function upsertPersonnelCostEmployeeSettings(
  login: string,
  payload: PersonnelCostEmployeeSettingsUpsert,
): Promise<PersonnelCostEmployeeSettings> {
  return api.put<PersonnelCostEmployeeSettings>(
    `/personnel-costs/employee-settings/${encodeURIComponent(login)}`,
    payload,
  );
}

export function settingsForLogin(
  settings: PersonnelCostEmployeeSettings[],
  login: string,
): PersonnelCostEmployeeSettings {
  const normalized = login.toLowerCase().split("@")[0] || login;
  const found = settings.find((s) => s.user_login.toLowerCase() === normalized);
  return (
    found ?? {
      user_login: normalized,
      ...DEFAULT_EMPLOYEE_SETTINGS,
      target_hourly_rate: null,
      department: null,
      updated_at: null,
      updated_by_login: null,
    }
  );
}

export function entriesForLogin(entries: PersonnelCostEntry[], login: string): PersonnelCostEntry[] {
  const normalized = login.toLowerCase().split("@")[0];
  return entries.filter((e) => {
    if (e.scope === "global" || e.scope === "department") return true;
    return (e.user_login ?? "").toLowerCase().split("@")[0] === normalized;
  });
}

export function globalEntries(entries: PersonnelCostEntry[]): PersonnelCostEntry[] {
  return entries.filter((e) => e.scope === "global");
}

export function departmentEntries(entries: PersonnelCostEntry[]): PersonnelCostEntry[] {
  return entries.filter((e) => e.scope === "department");
}

export function employeeEntries(entries: PersonnelCostEntry[], login: string): PersonnelCostEntry[] {
  const normalized = login.toLowerCase().split("@")[0];
  return entries.filter(
    (e) => e.scope === "employee" && (e.user_login ?? "").toLowerCase().split("@")[0] === normalized,
  );
}

export function formatEuro(value: number, decimals = 2): string {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(Number.isFinite(value) ? value : 0);
}

export function formatEuroPerHour(value: number): string {
  return `${formatEuro(value)}/h`;
}
